import {
  actionsToVector,
  buildControlFrame,
  type ControlAction,
  type ControlFrame,
} from '@digger/shared';

export interface TickerOptions {
  tickHz: number;
  /** Sample the *current* input state — the ticker never queues stale input. */
  sampleActions: () => ReadonlySet<ControlAction>;
  sampleEstop: () => boolean;
  send: (frame: ControlFrame) => void;
  now?: () => number;
}

/**
 * Fixed-rate control loop: every tick, sample the live input state, build a
 * frame with a fresh seq + timestamp, and hand it to the transport. There is
 * no buffer anywhere — if the transport can't send, the frame is dropped and
 * the next tick supersedes it (the agent's deadman covers the gap).
 */
export class ControlTicker {
  private seq = 0;
  private timer: ReturnType<typeof setInterval> | undefined;
  private readonly now: () => number;

  constructor(private readonly opts: TickerOptions) {
    this.now = opts.now ?? (() => Date.now());
  }

  start(): void {
    this.stop();
    this.timer = setInterval(() => this.tick(), 1000 / this.opts.tickHz);
  }

  stop(): void {
    if (this.timer !== undefined) clearInterval(this.timer);
    this.timer = undefined;
  }

  get running(): boolean {
    return this.timer !== undefined;
  }

  /** One control tick. Exposed for tests and for forced immediate sends. */
  tick(): ControlFrame {
    const vector = actionsToVector(new Set(this.opts.sampleActions()));
    const frame = buildControlFrame(vector, this.seq++, this.opts.sampleEstop(), this.now());
    this.opts.send(frame);
    return frame;
  }
}
